import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Button,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Switch, FormControlLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab, Badge, Tooltip, Divider, Paper, Avatar, LinearProgress, Pagination,
  Checkbox, ListItemText, OutlinedInput, SelectChangeEvent,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Close as CloseIcon, Refresh as RefreshIcon,
  Notifications as NotifIcon, Schedule as ScheduleIcon,
  Sms as SmsIcon, Email as EmailIcon, WhatsApp as WhatsAppIcon,
  CheckCircle as ConfirmedIcon, Cancel as CancelledIcon,
  Send as SendIcon, History as HistoryIcon, Settings as SettingsIcon,
  Dashboard as DashboardIcon, TrendingUp as TrendingIcon,
  Warning as WarningIcon, AccessTime as TimeIcon,
  Person as PersonIcon, LocalHospital as HospitalIcon,
  ContentCopy as CopyIcon, Visibility as ViewIcon,
  PlayArrow as PlayIcon, Pause as PauseIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useClinicPageBreakpoints, clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';


// ---- Types ----

interface ReminderRule {
  id: string;
  name: string;
  trigger_type: string;
  channel: string;
  time_before_hours: number;
  appointment_types: string[] | null;
  message_template: string;
  email_subject: string;
  is_active: boolean;
  send_to_patient: boolean;
  send_to_professional: boolean;
  max_attempts: number;
  priority: number;
  created_at: string;
  updated_at: string;
}

interface ReminderStats {
  total_sent: number;
  total_confirmed: number;
  total_cancelled: number;
  total_pending: number;
  total_failed: number;
  confirmation_rate: string;
}

interface HistoryItem {
  id: string;
  status: string;
  channel: string;
  reminder_type: string;
  scheduled_at: string;
  sent_at: string | null;
  patient_response: string | null;
  message_content: string | null;
  error_message: string | null;
  attempt_count: number;
  clinic_appointments: {
    appointment_date: string;
    start_time: string;
    appointment_type: string;
    title: string | null;
    clinic_patients: { id: string; full_name: string; phone: string; whatsapp: string; email: string };
    clinic_professionals: { id: string; full_name: string } | null;
  };
  clinic_reminder_rules: { name: string; trigger_type: string; channel: string } | null;
}

// ---- Constants ----

const channelIcons: Record<string, React.ReactNode> = {
  whatsapp: <WhatsAppIcon sx={{ color: '#25D366' }} />,
  email: <EmailIcon sx={{ color: '#EA4335' }} />,
  ambos: <SendIcon sx={{ color: '#14B8A6' }} />,
};

const channelLabels: Record<string, string> = {
  whatsapp: 'WhatsApp', email: 'E-mail', ambos: 'WhatsApp + E-mail',
};

const triggerLabels: Record<string, string> = {
  before_appointment: 'Antes da Consulta',
  appointment_confirmation: 'Confirmação',
  follow_up: 'Pós-consulta',
  birthday: 'Aniversário',
  payment_due: 'Pagamento Pendente',
};

const appointmentTypeLabels: Record<string, string> = {
  consultation: 'Consulta',
  return: 'Retorno',
  procedure: 'Procedimento',
  exam: 'Exame',
  emergency: 'Emergência',
  surgery: 'Cirurgia',
};

const ALL_APPOINTMENT_TYPES = Object.keys(appointmentTypeLabels);

const statusColors: Record<string, string> = {
  scheduled: '#f59e0b',
  sent: '#3b82f6',
  delivered: '#22c55e',
  failed: '#ef4444',
  cancelled: '#64748b',
};

const statusLabels: Record<string, string> = {
  scheduled: 'Na fila',
  sent: 'Enviado',
  delivered: 'Entregue',
  failed: 'Falhou',
  cancelled: 'Cancelado (fila)',
};

const responseLabels: Record<string, string> = {
  confirmed: 'Confirmou',
  cancelled: 'Cancelou',
  rescheduled: 'Remarcou',
  no_response: 'Sem resposta',
};

const DEFAULT_TEMPLATES: Record<string, string> = {
  consultation: 'Olá {paciente}! 😊 Lembramos que você tem uma *consulta* agendada para *{data}* às *{hora}* com *{profissional}*.\n\nConfirme, reagende ou cancele pelo link abaixo:\n👉 {link}',
  return: 'Olá {paciente}! 😊 Lembramos do seu *retorno* agendado para *{data}* às *{hora}* com *{profissional}*.\n\nConfirme sua presença:\n👉 {link}',
  procedure: 'Olá {paciente}! 😊 Lembramos que você tem um *procedimento* ({procedimento}) agendado para *{data}* às *{hora}*.\n\nConfirme pelo link:\n👉 {link}',
  exam: 'Olá {paciente}! 😊 Lembramos que você tem um *exame* agendado para *{data}* às *{hora}*.\n\nConfirme pelo link:\n👉 {link}',
  default: 'Olá {paciente}! 😊 Lembramos que você tem um agendamento para *{data}* às *{hora}* com *{profissional}*.\n\nConfirme, reagende ou cancele:\n👉 {link}',
};

const TEMPLATE_VARS = [
  { var: '{paciente}', desc: 'Nome do paciente' },
  { var: '{data}', desc: 'Data da consulta' },
  { var: '{hora}', desc: 'Hora da consulta' },
  { var: '{profissional}', desc: 'Nome do profissional' },
  { var: '{tipo_consulta}', desc: 'Tipo (Consulta, Retorno...)' },
  { var: '{procedimento}', desc: 'Procedimento/título' },
  { var: '{especialidade}', desc: 'Especialidade do profissional' },
  { var: '{clinica}', desc: 'Nome da clínica' },
  { var: '{link}', desc: 'Link de confirmação (Confirmar/Reagendar/Cancelar)' },
];

// ---- Component ----

const ReminderSettings: React.FC = () => {
  const { isMobile } = useClinicPageBreakpoints();

  const [activeTab, setActiveTab] = useState(0);
  const [rules, setRules] = useState<ReminderRule[]>([]);
  const [stats, setStats] = useState<ReminderStats | null>(null);
  const [globalSettings, setGlobalSettings] = useState<any>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyFilter, setHistoryFilter] = useState({ status: '', channel: '' });
  const [loading, setLoading] = useState(true);
  const [sendingReminderId, setSendingReminderId] = useState<string | null>(null);
  const [cancellingReminderId, setCancellingReminderId] = useState<string | null>(null);
  const [bulkCancelling, setBulkCancelling] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<ReminderRule | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as any });
  const [previewOpen, setPreviewOpen] = useState(false);

  const [form, setForm] = useState({
    name: '',
    trigger_type: 'before_appointment',
    channel: 'whatsapp',
    time_before_hours: 24,
    appointment_types: [] as string[],
    message_template: DEFAULT_TEMPLATES.default,
    email_subject: 'Lembrete de Consulta - {clinica}',
    is_active: true,
    send_to_patient: true,
    send_to_professional: false,
    max_attempts: 3,
    priority: 0,
  });

  // ---- Data loading ----
  // getApiUrl() já inclui /api/v1 — usar /clinic/... (não /api/clinic/...)

  const loadRules = useCallback(async () => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/reminder-rules'));
      setRules(data.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/reminders/stats'));
      setStats(data.data);
    } catch (e) { console.error(e); }
  }, []);

  const loadGlobalSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/reminder-settings'));
      setGlobalSettings(data.data);
    } catch (e) { console.error(e); }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const params: any = { limit: 20, offset: (historyPage - 1) * 20 };
      if (historyFilter.status) params.status = historyFilter.status;
      if (historyFilter.channel) params.channel = historyFilter.channel;
      const { data } = await axios.get(getApiUrl('/clinic/reminders/history'), { params });
      setHistory(data.data || []);
      setHistoryTotal(data.total || 0);
    } catch (e) { console.error(e); }
  }, [historyPage, historyFilter]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([loadRules(), loadStats(), loadGlobalSettings()]);
      setLoading(false);
    };
    load();
  }, [loadRules, loadStats, loadGlobalSettings]);

  useEffect(() => {
    if (activeTab === 2) {
      loadHistory();
      loadStats();
    }
  }, [activeTab, loadHistory, loadStats]);

  const handleForceSend = async (reminderId: string) => {
    try {
      setSendingReminderId(reminderId);
      const { data } = await axios.post(getApiUrl(`/clinic/reminders/${reminderId}/send`));
      if (data.success) {
        setSnackbar({ open: true, message: 'Lembrete enviado com sucesso!', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: data.error || 'Erro ao enviar', severity: 'error' });
      }
      await loadHistory();
      await loadStats();
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.response?.data?.error || 'Erro ao enviar lembrete', severity: 'error' });
    } finally {
      setSendingReminderId(null);
    }
  };

  const handleCancelQueueItem = async (reminderId: string) => {
    try {
      setCancellingReminderId(reminderId);
      await axios.post(getApiUrl(`/clinic/reminders/${reminderId}/cancel`));
      setSnackbar({ open: true, message: 'Envio retirado da fila.', severity: 'success' });
      await loadHistory();
      await loadStats();
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: e?.response?.data?.error || 'Erro ao cancelar envio',
        severity: 'error',
      });
    } finally {
      setCancellingReminderId(null);
    }
  };

  const handleCancelQueuedAll = async () => {
    if (!stats?.total_pending || stats.total_pending < 1) {
      setSnackbar({ open: true, message: 'Não há envios na fila para cancelar.', severity: 'info' });
      return;
    }
    if (
      !window.confirm(
        `Tem certeza? Serão cancelados ${stats.total_pending} envio(s) ainda AGENDADO(S) NA FILA (todos). ` +
          'Agendamentos de consulta em si não são cancelados.'
      )
    )
      return;
    try {
      setBulkCancelling(true);
      const { data } = await axios.post(getApiUrl('/clinic/reminders/queue/cancel-all'));
      if (data.success) {
        const n = typeof data.cancelled === 'number' ? data.cancelled : 0;
        setSnackbar({
          open: true,
          message: n ? `${n} envio(s) removidos da fila.` : 'Nenhum envio pendente na fila.',
          severity: 'success',
        });
      } else {
        setSnackbar({ open: true, message: data.error || 'Erro ao cancelar fila', severity: 'error' });
      }
      await loadHistory();
      await loadStats();
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: e?.response?.data?.error || 'Erro ao cancelar fila',
        severity: 'error',
      });
    } finally {
      setBulkCancelling(false);
    }
  };

  const refreshAll = async () => {
    setLoading(true);
    await Promise.all([loadRules(), loadStats(), loadGlobalSettings()]);
    if (activeTab === 2) await loadHistory();
    setLoading(false);
  };

  // ---- Rule CRUD ----

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
        appointment_types: form.appointment_types.length > 0 ? form.appointment_types : null,
      };
      if (editing) {
        await axios.put(getApiUrl(`/clinic/reminder-rules/${editing.id}`), payload);
        setSnackbar({ open: true, message: 'Regra atualizada com sucesso!', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/clinic/reminder-rules'), payload);
        setSnackbar({ open: true, message: 'Regra criada com sucesso!', severity: 'success' });
      }
      setOpenDialog(false);
      resetForm();
      loadRules();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.error || 'Erro ao salvar', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta regra?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/reminder-rules/${id}`));
      setSnackbar({ open: true, message: 'Regra excluída!', severity: 'success' });
      loadRules();
    } catch (e) {
      setSnackbar({ open: true, message: 'Erro ao excluir', severity: 'error' });
    }
  };

  const handleToggle = async (rule: ReminderRule) => {
    try {
      await axios.put(getApiUrl(`/clinic/reminder-rules/${rule.id}`), { is_active: !rule.is_active });
      loadRules();
    } catch (e) { console.error(e); }
  };

  const handleEdit = (rule: ReminderRule) => {
    setEditing(rule);
    setForm({
      name: rule.name || '',
      trigger_type: rule.trigger_type || 'before_appointment',
      channel: rule.channel || 'whatsapp',
      time_before_hours: rule.time_before_hours || 24,
      appointment_types: rule.appointment_types || [],
      message_template: rule.message_template || DEFAULT_TEMPLATES.default,
      email_subject: rule.email_subject || 'Lembrete de Consulta - {clinica}',
      is_active: rule.is_active !== false,
      send_to_patient: rule.send_to_patient !== false,
      send_to_professional: rule.send_to_professional || false,
      max_attempts: rule.max_attempts || 3,
      priority: rule.priority || 0,
    });
    setOpenDialog(true);
  };

  const resetForm = () => {
    setEditing(null);
    setForm({
      name: '', trigger_type: 'before_appointment', channel: 'whatsapp',
      time_before_hours: 24, appointment_types: [],
      message_template: DEFAULT_TEMPLATES.default,
      email_subject: 'Lembrete de Consulta - {clinica}',
      is_active: true, send_to_patient: true, send_to_professional: false,
      max_attempts: 3, priority: 0,
    });
  };

  const handleSaveGlobalSettings = async () => {
    try {
      await axios.put(getApiUrl('/clinic/reminder-settings'), globalSettings);
      setSnackbar({ open: true, message: 'Configurações globais salvas!', severity: 'success' });
      loadGlobalSettings();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.error || 'Erro ao salvar', severity: 'error' });
    }
  };

  const handleApptTypesChange = (event: SelectChangeEvent<string[]>) => {
    const val = event.target.value;
    setForm({ ...form, appointment_types: typeof val === 'string' ? val.split(',') : val });
  };

  const previewMessage = () => {
    return form.message_template
      .replace(/\{paciente\}/g, 'Maria Silva')
      .replace(/\{nome_paciente\}/g, 'Maria Silva')
      .replace(/\{data\}/g, 'segunda-feira, 31/03/2026')
      .replace(/\{data_consulta\}/g, 'segunda-feira, 31/03/2026')
      .replace(/\{hora\}/g, '14:30')
      .replace(/\{hora_consulta\}/g, '14:30')
      .replace(/\{profissional\}/g, 'Dr. João Santos')
      .replace(/\{nome_profissional\}/g, 'Dr. João Santos')
      .replace(/\{tipo_consulta\}/g, 'Consulta')
      .replace(/\{procedimento\}/g, 'Consulta de rotina')
      .replace(/\{especialidade\}/g, 'Clínico Geral')
      .replace(/\{link\}/g, 'https://suaclínica.com/confirmar/abc123')
      .replace(/\{link_confirmar\}/g, 'https://suaclínica.com/confirmar/abc123')
      .replace(/\{clinica\}/g, 'Clínica Saúde');
  };

  // ---- Render helpers ----

  const KpiCard = ({ label, value, color, icon, subtitle }: { label: string; value: number | string; color: string; icon: React.ReactNode; subtitle?: string }) => (
    <Card sx={{ bgcolor: 'white', borderRadius: 3, border: `1px solid ${color}22`, height: '100%' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ bgcolor: `${color}15`, color, width: 44, height: 44 }}>{icon}</Avatar>
        <Box>
          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>{label}</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color, lineHeight: 1.2 }}>{value}</Typography>
          {subtitle && <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>{subtitle}</Typography>}
        </Box>
      </CardContent>
    </Card>
  );

  if (loading && rules.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress sx={{ color: '#14B8A6' }} />
      </Box>
    );
  }

  return (
    <Box sx={clinicPageContainerSx}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotifIcon sx={{ color: '#14B8A6' }} />
            Confirmação Automática
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Gerencie regras de lembrete e confirmação automática de consultas via WhatsApp e e-mail
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Atualizar"><IconButton onClick={refreshAll} sx={{ color: '#14B8A6' }}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<AddIcon />}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', borderRadius: 2 }}
            onClick={() => { resetForm(); setOpenDialog(true); }}>
            Nova Regra
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{
            px: 1,
            '& .MuiTab-root': { minHeight: 52, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', color: '#64748b', '&.Mui-selected': { color: '#14B8A6' } },
            '& .MuiTabs-indicator': { backgroundColor: '#14B8A6', height: 3, borderRadius: '3px 3px 0 0' },
          }}>
          <Tab icon={<DashboardIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Dashboard" />
          <Tab icon={<Badge badgeContent={rules.length} color="primary" sx={{ '& .MuiBadge-badge': { bgcolor: '#14B8A6' } }}><NotifIcon sx={{ fontSize: 18 }} /></Badge>} iconPosition="start" label="Regras" />
          <Tab icon={<HistoryIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Histórico" />
          <Tab icon={<SettingsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Config. Global" />
        </Tabs>
      </Card>

      {/* ============================================ */}
      {/* TAB 0: DASHBOARD */}
      {/* ============================================ */}
      {activeTab === 0 && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard label="Enviados" value={stats?.total_sent || 0} color="#3b82f6" icon={<SendIcon />} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard label="Confirmados" value={stats?.total_confirmed || 0} color="#22c55e" icon={<ConfirmedIcon />} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard label="Cancelados" value={stats?.total_cancelled || 0} color="#ef4444" icon={<CancelledIcon />} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard label="Na Fila" value={stats?.total_pending || 0} color="#f59e0b" icon={<TimeIcon />} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard label="Falharam" value={stats?.total_failed || 0} color="#ef4444" icon={<WarningIcon />} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard label="Taxa Confirmação" value={`${stats?.confirmation_rate || 0}%`} color="#14B8A6" icon={<TrendingIcon />} />
            </Grid>
          </Grid>

          {/* Regras ativas resumo */}
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#0f172a' }}>Regras Ativas ({rules.filter(r => r.is_active).length})</Typography>
          <Grid container spacing={2}>
            {rules.filter(r => r.is_active).map(rule => (
              <Grid item xs={12} sm={6} md={4} key={rule.id}>
                <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0', height: '100%' }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>{rule.name}</Typography>
                      {channelIcons[rule.channel]}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                      <Chip label={triggerLabels[rule.trigger_type] || rule.trigger_type} size="small" sx={{ bgcolor: '#14B8A615', color: '#14B8A6', fontWeight: 600, fontSize: '0.65rem' }} />
                      <Chip label={`${rule.time_before_hours}h antes`} size="small" sx={{ bgcolor: '#3b82f615', color: '#3b82f6', fontWeight: 600, fontSize: '0.65rem' }} />
                    </Box>
                    {rule.appointment_types && rule.appointment_types.length > 0 ? (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {rule.appointment_types.map(t => (
                          <Chip key={t} label={appointmentTypeLabels[t] || t} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>Todos os tipos de consulta</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
            {rules.filter(r => r.is_active).length === 0 && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  Nenhuma regra ativa. Crie uma nova regra para começar a enviar confirmações automáticas.
                </Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* ============================================ */}
      {/* TAB 1: REGRAS */}
      {/* ============================================ */}
      {activeTab === 1 && (
        <Card sx={{ borderRadius: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Regra</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Gatilho</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Canal</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Antecedência</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Tipos de Consulta</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Destinatários</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, color: '#94a3b8' }}>
                      <NotifIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                      <Typography>Nenhuma regra configurada</Typography>
                      <Button startIcon={<AddIcon />} onClick={() => { resetForm(); setOpenDialog(true); }}
                        sx={{ mt: 1, color: '#14B8A6', textTransform: 'none' }}>
                        Criar primeira regra
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : rules.map(rule => (
                  <TableRow key={rule.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{rule.name}</Typography>
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>Prioridade: {rule.priority}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={triggerLabels[rule.trigger_type] || rule.trigger_type} size="small"
                        sx={{ bgcolor: '#14B8A615', color: '#14B8A6', fontWeight: 600, fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {channelIcons[rule.channel]}
                        <Typography variant="body2">{channelLabels[rule.channel] || rule.channel}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={`${rule.time_before_hours}h`} size="small" sx={{ bgcolor: '#3b82f615', color: '#3b82f6', fontWeight: 600 }} />
                    </TableCell>
                    <TableCell>
                      {rule.appointment_types && rule.appointment_types.length > 0 ? (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {rule.appointment_types.map(t => (
                            <Chip key={t} label={appointmentTypeLabels[t] || t} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 22 }} />
                          ))}
                        </Box>
                      ) : (
                        <Chip label="Todos" size="small" sx={{ bgcolor: '#f1f5f9', fontSize: '0.65rem' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {rule.send_to_patient && <Chip icon={<PersonIcon sx={{ fontSize: 14 }} />} label="Paciente" size="small" sx={{ fontSize: '0.6rem', height: 22 }} />}
                        {rule.send_to_professional && <Chip icon={<HospitalIcon sx={{ fontSize: 14 }} />} label="Profissional" size="small" sx={{ fontSize: '0.6rem', height: 22 }} />}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Switch checked={rule.is_active} onChange={() => handleToggle(rule)} size="small"
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#14B8A6' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#14B8A6' } }} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => handleEdit(rule)} sx={{ color: '#14B8A6' }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Excluir"><IconButton size="small" onClick={() => handleDelete(rule.id)} sx={{ color: '#ef4444' }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* ============================================ */}
      {/* TAB 2: HISTÓRICO */}
      {/* ============================================ */}
      {activeTab === 2 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select value={historyFilter.status} label="Status" onChange={(e) => { setHistoryFilter({ ...historyFilter, status: e.target.value }); setHistoryPage(1); }}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="scheduled">Na fila</MenuItem>
                <MenuItem value="sent">Enviado</MenuItem>
                <MenuItem value="failed">Falhou</MenuItem>
                <MenuItem value="cancelled">Cancelado (fila)</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Canal</InputLabel>
              <Select value={historyFilter.channel} label="Canal" onChange={(e) => { setHistoryFilter({ ...historyFilter, channel: e.target.value }); setHistoryPage(1); }}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
                <MenuItem value="email">E-mail</MenuItem>
              </Select>
            </FormControl>
            <Chip label={`${historyTotal} registros`} sx={{ bgcolor: '#14B8A615', color: '#14B8A6', fontWeight: 600, alignSelf: 'center' }} />
            <Tooltip title={`Cancelar todos os envios na fila (pendentes — ${stats?.total_pending ?? 0} neste momento). Não altera consultas.`}>
              <span style={{ alignSelf: 'center' }}>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  disabled={bulkCancelling || !stats?.total_pending}
                  onClick={handleCancelQueuedAll}
                  startIcon={
                    bulkCancelling ? <CircularProgress color="inherit" size={14} sx={{ mr: -0.5 }} /> : <CancelledIcon fontSize="small" />
                  }
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    alignSelf: 'center',
                  }}
                >
                  {bulkCancelling ? 'Cancelando…' : 'Cancelar todos na fila'}
                </Button>
              </span>
            </Tooltip>
          </Box>

          <Card sx={{ borderRadius: 3 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Paciente</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Consulta</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Regra</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Canal</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Resposta</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Agendado</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Enviado</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                        Nenhum registro encontrado
                      </TableCell>
                    </TableRow>
                  ) : history.map(item => {
                    const apt = item.clinic_appointments;
                    const patient = apt?.clinic_patients;
                    const prof = apt?.clinic_professionals;
                    const rule = item.clinic_reminder_rules;
                    return (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{patient?.full_name || '-'}</Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>{patient?.whatsapp || patient?.phone || patient?.email || '-'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {apt?.appointment_date ? new Date(apt.appointment_date + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {apt?.start_time?.substring(0, 5)} - {appointmentTypeLabels[apt?.appointment_type] || apt?.appointment_type}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>{rule?.name || '-'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {channelIcons[item.channel] || null}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={statusLabels[item.status] || item.status} size="small"
                            sx={{ bgcolor: `${statusColors[item.status] || '#94a3b8'}15`, color: statusColors[item.status] || '#94a3b8', fontWeight: 600, fontSize: '0.65rem' }} />
                          {item.error_message && (
                            <Tooltip title={item.error_message}><WarningIcon sx={{ fontSize: 14, color: '#ef4444', ml: 0.5 }} /></Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          {item.patient_response ? (
                            <Chip label={responseLabels[item.patient_response] || item.patient_response} size="small"
                              sx={{ bgcolor: item.patient_response === 'confirmed' ? '#22c55e15' : '#ef444415', color: item.patient_response === 'confirmed' ? '#22c55e' : '#ef4444', fontWeight: 600, fontSize: '0.65rem' }} />
                          ) : (
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{item.scheduled_at ? new Date(item.scheduled_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{item.sent_at ? new Date(item.sent_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              flexWrap: 'wrap',
                              justifyContent: 'center',
                              minHeight: 32,
                            }}
                          >
                            {item.status === 'scheduled' && (
                              <Tooltip title="Retirar da fila (não envia)">
                                <IconButton
                                  size="small"
                                  disabled={cancellingReminderId === item.id}
                                  onClick={() => {
                                    if (!window.confirm('Retirar este envio da fila? Ele não será enviado pelo sistema.'))
                                      return;
                                    handleCancelQueueItem(item.id);
                                  }}
                                  sx={{ color: '#64748b', '&:hover': { bgcolor: 'rgba(100,116,139,0.08)' } }}
                                >
                                  {cancellingReminderId === item.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <CloseIcon sx={{ fontSize: 17 }} />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                            {(item.status === 'scheduled' || item.status === 'failed') && (
                              <Tooltip title={item.status === 'scheduled' ? 'Enviar agora' : 'Reenviar'}>
                                <IconButton
                                  size="small"
                                  disabled={sendingReminderId === item.id}
                                  onClick={() => handleForceSend(item.id)}
                                  sx={{ color: '#14B8A6', '&:hover': { bgcolor: '#14B8A615' } }}
                                >
                                  {sendingReminderId === item.id ? <CircularProgress size={16} /> : <SendIcon sx={{ fontSize: 16 }} />}
                                </IconButton>
                              </Tooltip>
                            )}
                            {item.status !== 'scheduled' && item.status !== 'failed' && (
                              <Typography variant="caption" sx={{ color: '#94a3b8' }}>-</Typography>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {historyTotal > 20 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <Pagination count={Math.ceil(historyTotal / 20)} page={historyPage} onChange={(_, p) => setHistoryPage(p)}
                  color="primary" size="small" />
              </Box>
            )}
          </Card>
        </Box>
      )}

      {/* ============================================ */}
      {/* TAB 3: CONFIG GLOBAL */}
      {/* ============================================ */}
      {activeTab === 3 && globalSettings && (
        <Card sx={{ borderRadius: 3, p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#0f172a' }}>Configurações Gerais</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={globalSettings.reminder_enabled || false}
                  onChange={(e) => setGlobalSettings({ ...globalSettings, reminder_enabled: e.target.checked })}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#14B8A6' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#14B8A6' } }} />}
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Lembretes habilitados</Typography>} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={globalSettings.confirmation_enabled || false}
                  onChange={(e) => setGlobalSettings({ ...globalSettings, confirmation_enabled: e.target.checked })}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#14B8A6' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#14B8A6' } }} />}
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Confirmação habilitada</Typography>} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={globalSettings.auto_cancel_no_response || false}
                  onChange={(e) => setGlobalSettings({ ...globalSettings, auto_cancel_no_response: e.target.checked })}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#ef4444' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#ef4444' } }} />}
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Auto-cancelar sem resposta</Typography>} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" label="Horas para auto-cancelar"
                value={globalSettings.auto_cancel_hours || 4}
                onChange={(e) => setGlobalSettings({ ...globalSettings, auto_cancel_hours: parseInt(e.target.value) || 4 })}
                disabled={!globalSettings.auto_cancel_no_response} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Canal Preferido</InputLabel>
                <Select value={globalSettings.preferred_channel || 'whatsapp'} label="Canal Preferido"
                  onChange={(e) => setGlobalSettings({ ...globalSettings, preferred_channel: e.target.value })}>
                  {Object.entries(channelLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={globalSettings.follow_up_enabled || false}
                  onChange={(e) => setGlobalSettings({ ...globalSettings, follow_up_enabled: e.target.checked })}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#14B8A6' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#14B8A6' } }} />}
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Pós-consulta habilitado</Typography>} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={globalSettings.birthday_enabled || false}
                  onChange={(e) => setGlobalSettings({ ...globalSettings, birthday_enabled: e.target.checked })}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#14B8A6' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#14B8A6' } }} />}
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Aniversário habilitado</Typography>} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={globalSettings.nps_enabled || false}
                  onChange={(e) => setGlobalSettings({ ...globalSettings, nps_enabled: e.target.checked })}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#14B8A6' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#14B8A6' } }} />}
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>NPS / Satisfação habilitado</Typography>} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={globalSettings.reactivation_enabled || false}
                  onChange={(e) => setGlobalSettings({ ...globalSettings, reactivation_enabled: e.target.checked })}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#14B8A6' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#14B8A6' } }} />}
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Reativação de inativos</Typography>} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" label="Dias para considerar inativo"
                value={globalSettings.reactivation_days_inactive || 180}
                onChange={(e) => setGlobalSettings({ ...globalSettings, reactivation_days_inactive: parseInt(e.target.value) || 180 })}
                disabled={!globalSettings.reactivation_enabled} />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="contained" onClick={handleSaveGlobalSettings}
              sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', borderRadius: 2 }}>
              Salvar Configurações
            </Button>
          </Box>
        </Card>
      )}

      {/* ============================================ */}
      {/* DIALOG: CRIAR/EDITAR REGRA */}
      {/* ============================================ */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{editing ? 'Editar' : 'Nova'} Regra de Confirmação</Typography>
          <IconButton onClick={() => setOpenDialog(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Nome da Regra" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex: Confirmação 24h - Consultas" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Gatilho</InputLabel>
                <Select value={form.trigger_type} label="Gatilho"
                  onChange={(e) => setForm({ ...form, trigger_type: e.target.value })}>
                  {Object.entries(triggerLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Canal de Envio</InputLabel>
                <Select value={form.channel} label="Canal de Envio"
                  onChange={(e) => setForm({ ...form, channel: e.target.value })}>
                  {Object.entries(channelLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" type="number" label="Antecedência (horas)"
                value={form.time_before_hours}
                onChange={(e) => setForm({ ...form, time_before_hours: parseInt(e.target.value) || 0 })}
                helperText="Ex: 24 = 1 dia antes" />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipos de Consulta</InputLabel>
                <Select multiple value={form.appointment_types} label="Tipos de Consulta"
                  onChange={handleApptTypesChange}
                  input={<OutlinedInput label="Tipos de Consulta" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map(v => <Chip key={v} label={appointmentTypeLabels[v] || v} size="small" sx={{ fontSize: '0.65rem' }} />)}
                    </Box>
                  )}>
                  {ALL_APPOINTMENT_TYPES.map(t => (
                    <MenuItem key={t} value={t}>
                      <Checkbox checked={form.appointment_types.includes(t)} size="small" />
                      <ListItemText primary={appointmentTypeLabels[t]} />
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.5, ml: 1 }}>
                  Vazio = aplica a todos os tipos
                </Typography>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="Prioridade"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })}
                helperText="Maior = primeiro" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="Tentativas máx."
                value={form.max_attempts}
                onChange={(e) => setForm({ ...form, max_attempts: parseInt(e.target.value) || 3 })} />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Template da Mensagem</Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {Object.entries(DEFAULT_TEMPLATES).filter(([k]) => k !== 'default').map(([k, v]) => (
                    <Chip key={k} label={appointmentTypeLabels[k] || k} size="small" variant="outlined"
                      onClick={() => setForm({ ...form, message_template: v })}
                      sx={{ cursor: 'pointer', fontSize: '0.6rem', '&:hover': { bgcolor: '#14B8A615' } }} />
                  ))}
                </Box>
              </Box>
              <TextField fullWidth size="small" multiline rows={5} value={form.message_template}
                onChange={(e) => setForm({ ...form, message_template: e.target.value })} />
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                {TEMPLATE_VARS.map(v => (
                  <Tooltip key={v.var} title={v.desc}>
                    <Chip label={v.var} size="small" variant="outlined"
                      onClick={() => setForm({ ...form, message_template: form.message_template + v.var })}
                      sx={{ cursor: 'pointer', fontSize: '0.6rem', '&:hover': { bgcolor: '#14B8A615' } }} />
                  </Tooltip>
                ))}
              </Box>
            </Grid>

            {(form.channel === 'email' || form.channel === 'ambos') && (
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Assunto do E-mail" value={form.email_subject}
                  onChange={(e) => setForm({ ...form, email_subject: e.target.value })} />
              </Grid>
            )}

            <Grid item xs={12}>
              <Button size="small" variant="outlined" onClick={() => setPreviewOpen(true)}
                sx={{ textTransform: 'none', borderColor: '#14B8A6', color: '#14B8A6' }} startIcon={<ViewIcon />}>
                Pré-visualizar mensagem
              </Button>
            </Grid>

            <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

            <Grid item xs={6}>
              <FormControlLabel
                control={<Switch checked={form.send_to_patient}
                  onChange={(e) => setForm({ ...form, send_to_patient: e.target.checked })}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#14B8A6' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#14B8A6' } }} />}
                label="Enviar ao Paciente" />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={<Switch checked={form.send_to_professional}
                  onChange={(e) => setForm({ ...form, send_to_professional: e.target.checked })}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#14B8A6' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#14B8A6' } }} />}
                label="Enviar ao Profissional" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name || !form.message_template}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', borderRadius: 2 }}>
            {editing ? 'Atualizar Regra' : 'Criar Regra'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: PRÉ-VISUALIZAÇÃO */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Pré-visualização da Mensagem</Typography>
          <IconButton onClick={() => setPreviewOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ bgcolor: '#e5ddd5', borderRadius: 2, p: 2, minHeight: 150 }}>
            <Box sx={{ bgcolor: '#dcf8c6', borderRadius: '10px 10px 0 10px', p: 1.5, maxWidth: '85%', ml: 'auto', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', lineHeight: 1.5 }}>
                {previewMessage()}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', color: '#667781', mt: 0.5 }}>
                14:30 ✓✓
              </Typography>
            </Box>
          </Box>
          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            Esta é uma simulação. As variáveis serão substituídas automaticamente pelos dados reais do agendamento.
          </Alert>
        </DialogContent>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReminderSettings;
